import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Gift, Sparkles, Pause, Play, SkipForward, X, RefreshCcw,
  ShoppingBag, Calendar, ChevronRight, Loader2, Heart, LogOut, Crown, Flower2
} from "lucide-react";
import { API } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

const SCENTS = [
  "Lavender", "Pineapple", "Coconut", "Lemon", "Frankincense", "Grapefruit",
  "Cookies & Vanilla", "Sandalwood", "Green Apple", "Strawberry", "Peach",
  "Blueberry Maple Pancakes", "Teakwood", "Rosemary",
];

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [loyalty, setLoyalty] = useState(null);
  const [membership, setMembership] = useState(null);
  const [orders, setOrders] = useState([]);
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [membershipLoading, setMembershipLoading] = useState(false);

  const load = async () => {
    try {
      const [l, m, o, n] = await Promise.all([
        axios.get(`${API}/loyalty/status`, { withCredentials: true }),
        axios.get(`${API}/membership/status`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/family-news`, { withCredentials: true }),
      ]);
      setLoyalty(l.data);
      setMembership(m.data.membership);
      setOrders(o.data.orders || []);
      setNews(n.data);
    } catch (err) {
      toast.error("Couldn't load your dashboard. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const doAction = async (action) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/membership/action`, { action }, { withCredentials: true });
      setMembership(r.data.membership);
      const msgs = {
        pause: "Membership paused.",
        resume: "Welcome back — membership resumed.",
        skip_next: "We'll skip your next box.",
        cancel: "Cancellation scheduled at period end.",
        reactivate: "Cancellation reversed.",
      };
      toast.success(msgs[action] || "Updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const swapScent = async (scent) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/membership/swap-scent`, { scent }, { withCredentials: true });
      setMembership((m) => (m ? { ...m, scent: r.data.scent } : m));
      toast.success(`Next box: ${scent}.`);
    } catch (err) {
      toast.error("Couldn't swap scent. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const startMembership = async () => {
    setMembershipLoading(true);
    try {
      const res = await axios.post(
        `${API}/checkout/session`,
        { items: [], kind: "membership", origin_url: window.location.origin, customer_email: user?.email },
        { withCredentials: true }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Couldn't open checkout.");
      setMembershipLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-cream">
        <Loader2 className="w-8 h-8 text-brand-moss animate-spin" />
      </div>
    );
  }

  const status = membership?.status || "inactive";
  const cancelAtPeriodEnd = membership?.cancel_at_period_end;
  const skipNext = membership?.skip_next_month;

  return (
    <section data-testid="dashboard-page" className="bg-brand-cream pt-12 pb-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.35em] text-[#B7574E]">Your Sanctuary</span>
            <h1 className="mt-2 font-serif text-4xl lg:text-5xl text-brand-moss leading-tight">
              Hello, <span className="italic">{user?.name || user?.email?.split("@")[0]}</span>.
            </h1>
            <p className="mt-2 text-brand-moss/70 text-sm">
              Signed in via <span className="capitalize">{user?.auth_provider}</span>.
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="dashboard-logout"
            className="inline-flex items-center gap-2 text-sm text-brand-moss/70 hover:text-brand-moss border border-brand-moss/20 rounded-full px-4 py-2 hover:bg-brand-moss/5 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Loyalty tracker */}
          <div data-testid="loyalty-card" className="lg:col-span-2 relative bg-gradient-to-br from-brand-moss to-brand-moss-deep text-brand-cream rounded-3xl p-8 lg:p-10 overflow-hidden shadow-xl">
            <img src="/assets/flower_bouquet.png" alt="" aria-hidden="true" className="absolute -top-6 -right-6 w-40 opacity-25 pointer-events-none" />
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-brand-gold">
              <Gift className="w-3.5 h-3.5" /> Loyalty Tracker
            </span>
            <h2 className="mt-3 font-serif text-3xl lg:text-4xl leading-tight">
              You&apos;re{" "}
              <span className="text-brand-gold">
                ${Math.max(0, (loyalty?.milestone ?? 100) - (loyalty?.spent_toward_next ?? 0)).toFixed(2)}
              </span>{" "}
              away from a free candle.
            </h2>
            <div className="mt-6">
              <div className="flex justify-between text-xs mb-2 text-brand-cream/75">
                <span>${(loyalty?.spent_toward_next || 0).toFixed(2)} spent this cycle</span>
                <span>${(loyalty?.milestone || 100).toFixed(2)} milestone</span>
              </div>
              <div className="h-3 w-full bg-brand-cream/15 rounded-full overflow-hidden">
                <div
                  data-testid="loyalty-progress-bar"
                  className="h-full bg-brand-gold rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, loyalty?.progress_percent || 0)}%` }}
                />
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1.5 bg-brand-cream/10 rounded-full px-3 py-1 border border-brand-cream/15">
                  <Sparkles className="w-3.5 h-3.5 text-brand-gold" />
                  {loyalty?.available_free_candles || 0} free candle{(loyalty?.available_free_candles || 0) === 1 ? "" : "s"} banked
                </span>
                <span className="inline-flex items-center gap-1.5 text-brand-cream/70">
                  Lifetime: <strong className="text-brand-cream">${(loyalty?.lifetime_spend || 0).toFixed(2)}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Family News */}
          <div data-testid="family-news-card" className="bg-brand-parchment border border-brand-moss/15 rounded-3xl p-7 lg:p-8 shadow-sm relative overflow-hidden">
            <img src="/assets/flower_pink.png" alt="" aria-hidden="true" className="absolute -bottom-6 -right-6 w-32 opacity-70 pointer-events-none" />
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#B7574E]">
              <Heart className="w-3.5 h-3.5 fill-current" /> Family News
            </span>
            <h3 className="mt-3 font-serif text-2xl text-brand-moss leading-tight">
              {news?.title || "A note from the studio"}
            </h3>
            <p className="mt-3 text-brand-moss/75 text-sm leading-relaxed">
              {news?.body}
            </p>
            {news?.signed && (
              <p className="mt-4 font-script text-2xl text-[#B7574E]">{news.signed}</p>
            )}
          </div>
        </div>

        {/* Membership Hub */}
        <div data-testid="membership-hub" className="mt-10 bg-brand-parchment border border-brand-moss/15 rounded-3xl p-8 lg:p-10 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#B7574E]">
                <Crown className="w-3.5 h-3.5" /> Membership Hub
              </span>
              <h2 className="mt-3 font-serif text-3xl lg:text-4xl text-brand-moss leading-tight">
                Circle of Intention
              </h2>
            </div>
            {membership ? (
              <span
                data-testid="membership-status-badge"
                className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] px-4 py-1.5 rounded-full ${
                  status === "active" && !cancelAtPeriodEnd ? "bg-brand-moss text-brand-gold" :
                  status === "paused" ? "bg-[#B7574E]/20 text-[#B7574E]" :
                  cancelAtPeriodEnd ? "bg-brand-moss/15 text-brand-moss" :
                  "bg-brand-moss/10 text-brand-moss/70"
                }`}
              >
                {cancelAtPeriodEnd ? "Ending soon" : status}
              </span>
            ) : (
              <span data-testid="membership-status-badge" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] px-4 py-1.5 rounded-full bg-brand-moss/10 text-brand-moss/70">
                Not a member yet
              </span>
            )}
          </div>

          {!membership ? (
            <div className="mt-8 grid md:grid-cols-2 gap-6 items-center">
              <div>
                <p className="text-brand-moss/75 leading-relaxed">
                  Join for $10/month for early scent access, exclusive perks, and a free candle at
                  every $100 milestone. Pause, skip, or swap scents whenever you like.
                </p>
              </div>
              <button
                data-testid="dashboard-start-membership"
                onClick={startMembership}
                disabled={membershipLoading}
                className="bg-brand-moss text-brand-gold hover:bg-brand-moss-light px-6 py-3.5 rounded-full text-sm tracking-wide transition-colors disabled:opacity-60"
              >
                {membershipLoading ? "Opening checkout…" : "Begin Membership ($10/mo)"}
              </button>
            </div>
          ) : (
            <>
              <div className="mt-8 grid sm:grid-cols-3 gap-4 text-sm">
                <div className="bg-brand-cream rounded-xl px-4 py-3 border border-brand-moss/10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-brand-moss/60">Next box date</p>
                  <p className="mt-1 font-serif text-lg text-brand-moss flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-brand-gold" />
                    {fmtDate(membership.next_billing_date)}
                  </p>
                </div>
                <div className="bg-brand-cream rounded-xl px-4 py-3 border border-brand-moss/10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-brand-moss/60">Current scent</p>
                  <p className="mt-1 font-serif text-lg text-brand-moss flex items-center gap-2">
                    <Flower2 className="w-4 h-4 text-brand-gold" />
                    {membership.scent || "—"}
                  </p>
                </div>
                <div className="bg-brand-cream rounded-xl px-4 py-3 border border-brand-moss/10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-brand-moss/60">Member since</p>
                  <p className="mt-1 font-serif text-lg text-brand-moss">
                    {fmtDate(membership.started_at)}
                  </p>
                </div>
              </div>

              {/* Skip / Cancel banners */}
              {skipNext && (
                <div className="mt-4 rounded-xl bg-[#B7574E]/10 border border-[#B7574E]/25 px-4 py-3 text-sm text-[#B7574E]">
                  Your next box will be skipped. Billing pushed to {fmtDate(membership.next_billing_date)}.
                </div>
              )}
              {cancelAtPeriodEnd && (
                <div className="mt-4 rounded-xl bg-brand-moss/10 border border-brand-moss/25 px-4 py-3 text-sm text-brand-moss">
                  Cancellation scheduled for {fmtDate(membership.current_period_end)}. You keep member
                  perks until then — <button
                    onClick={() => doAction("reactivate")}
                    disabled={busy}
                    data-testid="reactivate-btn"
                    className="underline underline-offset-2 font-medium"
                  >
                    change your mind?
                  </button>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                {status === "active" ? (
                  <button data-testid="pause-btn" disabled={busy} onClick={() => doAction("pause")} className="flex items-center justify-center gap-2 bg-brand-cream border border-brand-moss/20 rounded-full py-3 text-sm text-brand-moss hover:bg-brand-moss/5">
                    <Pause className="w-4 h-4" /> Pause
                  </button>
                ) : (
                  <button data-testid="resume-btn" disabled={busy} onClick={() => doAction("resume")} className="flex items-center justify-center gap-2 bg-brand-moss text-brand-gold rounded-full py-3 text-sm">
                    <Play className="w-4 h-4" /> Resume
                  </button>
                )}
                <button data-testid="skip-btn" disabled={busy || skipNext} onClick={() => doAction("skip_next")} className="flex items-center justify-center gap-2 bg-brand-cream border border-brand-moss/20 rounded-full py-3 text-sm text-brand-moss hover:bg-brand-moss/5 disabled:opacity-50">
                  <SkipForward className="w-4 h-4" /> Skip next
                </button>
                {!cancelAtPeriodEnd ? (
                  <button data-testid="cancel-btn" disabled={busy} onClick={() => doAction("cancel")} className="flex items-center justify-center gap-2 bg-brand-cream border border-brand-moss/20 rounded-full py-3 text-sm text-brand-moss hover:bg-brand-moss/5">
                    <X className="w-4 h-4" /> Cancel
                  </button>
                ) : (
                  <button data-testid="reactivate-btn-2" disabled={busy} onClick={() => doAction("reactivate")} className="flex items-center justify-center gap-2 bg-brand-moss text-brand-gold rounded-full py-3 text-sm">
                    <RefreshCcw className="w-4 h-4" /> Reactivate
                  </button>
                )}
                <a
                  href="mailto:414llc.info@gmail.com?subject=Shipping%20update"
                  className="flex items-center justify-center gap-2 bg-brand-cream border border-brand-moss/20 rounded-full py-3 text-sm text-brand-moss hover:bg-brand-moss/5"
                  data-testid="update-shipping"
                >
                  Update shipping
                </a>
              </div>

              {/* Scent swap */}
              <div className="mt-8">
                <p className="text-xs uppercase tracking-[0.3em] text-brand-moss/60">Swap next month&apos;s scent</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SCENTS.map((s) => (
                    <button
                      key={s}
                      data-testid={`swap-${s.toLowerCase().replace(/\s+&\s+|\s+/g, "-")}`}
                      disabled={busy}
                      onClick={() => swapScent(s)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        membership.scent === s
                          ? "bg-brand-moss text-brand-gold border-brand-moss"
                          : "bg-brand-cream text-brand-moss border-brand-moss/20 hover:bg-brand-moss/5"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Orders */}
        <div data-testid="orders-card" className="mt-10 bg-brand-cream border border-brand-moss/15 rounded-3xl p-8 lg:p-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#B7574E]">
            <ShoppingBag className="w-3.5 h-3.5" /> Purchase History
          </span>
          <h2 className="mt-3 font-serif text-3xl text-brand-moss leading-tight">Your orders</h2>
          {orders.length === 0 ? (
            <p className="mt-6 text-brand-moss/60 text-sm">
              No orders yet.{" "}
              <Link to="/shop" className="text-brand-moss border-b border-brand-gold pb-0.5">Start browsing the shop</Link>.
            </p>
          ) : (
            <div className="mt-6 divide-y divide-brand-moss/10">
              {orders.map((o) => (
                <div key={o.session_id} data-testid={`order-${o.session_id}`} className="py-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg text-brand-moss">${Number(o.amount).toFixed(2)}</p>
                    <p className="text-xs text-brand-moss/60">
                      {fmtDate(o.created_at)} · {o.kind === "membership" ? "Membership" : `${(o.items || []).length} item${(o.items||[]).length === 1 ? "" : "s"}`}
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full ${
                    o.payment_status === "paid" ? "bg-brand-moss text-brand-gold" :
                    o.payment_status === "initiated" ? "bg-brand-parchment text-brand-moss/70" :
                    "bg-brand-parchment text-brand-moss/70"
                  }`}>
                    {o.payment_status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="mt-12 text-center text-sm text-brand-moss/60">
          Need something else?{" "}
          <Link to="/connect" className="text-brand-moss border-b border-brand-gold pb-0.5 inline-flex items-center gap-1">
            Write to Chatoria <ChevronRight className="w-3 h-3" />
          </Link>
        </p>
      </div>
    </section>
  );
}
